/**
 * ReferralCodeInput Component
 *
 * Input field for entering and validating referral codes during subscription.
 * Shows applied discount when a valid code is entered.
 */

import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet, ActivityIndicator } from 'react-native';
import { Gift, Check, X, Tag, Percent, Link } from 'lucide-react-native';
import Animated, { FadeIn, FadeInDown, FadeOut, Layout } from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';

import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import { useReferralStore, REFERRAL_DISCOUNT_PERCENT, isValidReferralCodeFormat } from '@/lib/referral';

// ============================================================================
// Types
// ============================================================================

interface ReferralCodeInputProps {
  userId: string;
  userType: 'startup' | 'investor';
  onDiscountApplied?: (discountPercent: number) => void;
  disabled?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function ReferralCodeInput({
  userId,
  userType,
  onDiscountApplied,
  disabled = false,
}: ReferralCodeInputProps) {
  const colors = useThemeColors();
  const [code, setCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const applyReferralCode = useReferralStore((s) => s.applyReferralCode);
  const appliedCode = useReferralStore((s) => s.appliedCode);
  const hasActiveDiscount = useReferralStore((s) => s.hasActiveDiscount);
  const getActiveReward = useReferralStore((s) => s.getActiveReward);
  const pendingCode = useReferralStore((s) => s.pendingCode);
  const clearPendingCode = useReferralStore((s) => s.clearPendingCode);

  // Check if user already has a discount (from referral or previous code)
  const activeReward = getActiveReward();
  const existingDiscount = hasActiveDiscount() ? activeReward?.value : null;

  // Auto-detect pending code from URL and pre-fill the input
  useEffect(() => {
    if (pendingCode && !appliedCode && isValidReferralCodeFormat(pendingCode)) {
      setCode(pendingCode);
      setIsExpanded(true);
    }
  }, [pendingCode, appliedCode]);

  const handleApplyCode = async () => {
    if (!code.trim() || isValidating || disabled) return;

    setIsValidating(true);
    setError(null);

    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);

    const result = await applyReferralCode(code.trim(), userId, userType);

    setIsValidating(false);

    if (result.success) {
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
      onDiscountApplied?.(REFERRAL_DISCOUNT_PERCENT);
      clearPendingCode(); // Clear pending code after successful application
      setCode('');
      setIsExpanded(false);
    } else {
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
      setError(result.error || 'Invalid code');
    }
  };

  const handleClearCode = () => {
    setCode('');
    setError(null);
  };

  // If already has an applied code, show success state
  if (appliedCode) {
    return (
      <Animated.View entering={FadeIn.duration(300)} layout={Layout.springify()}>
        <View
          style={[
            styles.appliedContainer,
            { backgroundColor: colors.traffic.greenLight, borderColor: colors.success },
          ]}
        >
          <View style={styles.appliedContent}>
            <View style={[styles.appliedIcon, { backgroundColor: colors.success }]}>
              <Check size={16} color="#fff" />
            </View>
            <View style={styles.appliedText}>
              <Text style={[styles.appliedTitle, { color: colors.success }]}>
                Referral Applied
              </Text>
              <Text style={[styles.appliedCode, { color: colors.text.secondary }]}>
                Code: {appliedCode}
              </Text>
            </View>
          </View>
          <View style={[styles.discountBadge, { backgroundColor: colors.success }]}>
            <Percent size={12} color="#fff" />
            <Text style={styles.discountText}>{REFERRAL_DISCOUNT_PERCENT}% OFF</Text>
          </View>
        </View>
      </Animated.View>
    );
  }

  // If user has an active reward from previous referrals
  if (existingDiscount) {
    return (
      <Animated.View entering={FadeIn.duration(300)} layout={Layout.springify()}>
        <View
          style={[
            styles.appliedContainer,
            { backgroundColor: colors.primary[50], borderColor: colors.primary[300] },
          ]}
        >
          <View style={styles.appliedContent}>
            <View style={[styles.appliedIcon, { backgroundColor: colors.primary[500] }]}>
              <Gift size={16} color="#fff" />
            </View>
            <View style={styles.appliedText}>
              <Text style={[styles.appliedTitle, { color: colors.primary[600] }]}>
                Referral Reward Active
              </Text>
              <Text style={[styles.appliedCode, { color: colors.text.secondary }]}>
                From your referral program
              </Text>
            </View>
          </View>
          <View style={[styles.discountBadge, { backgroundColor: colors.primary[500] }]}>
            <Percent size={12} color="#fff" />
            <Text style={styles.discountText}>{existingDiscount}% OFF</Text>
          </View>
        </View>
      </Animated.View>
    );
  }

  // Collapsed state - just a toggle button (or show pending code from link)
  if (!isExpanded) {
    // Show special state if there's a pending code from URL
    if (pendingCode && isValidReferralCodeFormat(pendingCode)) {
      return (
        <Animated.View entering={FadeIn.duration(300)} layout={Layout.springify()}>
          <Pressable
            onPress={() => {
              Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
              setCode(pendingCode);
              setIsExpanded(true);
            }}
            disabled={disabled}
            style={({ pressed }) => [
              styles.toggleButton,
              {
                backgroundColor: colors.primary[50],
                borderColor: colors.primary[300],
                opacity: pressed ? 0.9 : disabled ? 0.6 : 1,
              },
            ]}
          >
            <View style={styles.toggleContent}>
              <Link size={18} color={colors.primary[600]} />
              <View>
                <Text style={[styles.toggleText, { color: colors.primary[600], fontWeight: '600' }]}>
                  Referral code detected
                </Text>
                <Text style={[styles.pendingCodeText, { color: colors.primary[500] }]}>
                  {pendingCode}
                </Text>
              </View>
            </View>
            <View style={[styles.applyBadge, { backgroundColor: colors.primary[500] }]}>
              <Text style={styles.applyBadgeText}>Apply</Text>
            </View>
          </Pressable>
        </Animated.View>
      );
    }

    return (
      <Animated.View entering={FadeIn.duration(300)} layout={Layout.springify()}>
        <Pressable
          onPress={() => {
            Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
            setIsExpanded(true);
          }}
          disabled={disabled}
          style={({ pressed }) => [
            styles.toggleButton,
            {
              backgroundColor: colors.surface.primary,
              borderColor: colors.border.medium,
              opacity: pressed ? 0.9 : disabled ? 0.6 : 1,
            },
          ]}
        >
          <View style={styles.toggleContent}>
            <View style={[styles.toggleIcon, { backgroundColor: `${colors.primary[500]}15` }]}>
              <Tag size={16} color={colors.primary[600]} />
            </View>
            <Text
              style={[styles.toggleText, { color: colors.text.primary, fontWeight: '600' }]}
              numberOfLines={1}
            >
              Have a referral code?
            </Text>
          </View>
          <View
            style={[
              styles.toggleActionPill,
              { backgroundColor: colors.primary[50], borderColor: colors.primary[200] },
            ]}
          >
            <Text style={[styles.toggleAction, { color: colors.primary[700] }]} numberOfLines={1}>
              Enter code
            </Text>
          </View>
        </Pressable>
      </Animated.View>
    );
  }

  // Expanded state - input field
  return (
    <Animated.View
      entering={FadeInDown.duration(300)}
      layout={Layout.springify()}
    >
      <View
        style={[
          styles.container,
          { backgroundColor: colors.surface.primary, borderColor: colors.border.light },
        ]}
      >
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Gift size={18} color={colors.primary[500]} />
            <Text style={[styles.headerTitle, { color: colors.text.primary }]}>
              Enter Referral Code
            </Text>
          </View>
          <AnimatedPressable
            onPress={() => {
              setIsExpanded(false);
              setError(null);
              setCode('');
            }}
            style={styles.closeButton}
          >
            <X size={18} color={colors.text.tertiary} />
          </AnimatedPressable>
        </View>

        {/* Input Row */}
        <View style={styles.inputRow}>
          <TextInput
            style={[
              styles.input,
              {
                backgroundColor: colors.surface.secondary,
                color: colors.text.primary,
                borderColor: error ? colors.error : colors.border.light,
              },
            ]}
            placeholder="e.g. INV-ABC123"
            placeholderTextColor={colors.text.tertiary}
            value={code}
            onChangeText={(text) => {
              setCode(text.toUpperCase());
              setError(null);
            }}
            autoCapitalize="characters"
            autoCorrect={false}
            editable={!disabled && !isValidating}
          />
          <AnimatedPressable
            onPress={handleApplyCode}
            disabled={!code.trim() || isValidating || disabled}
            style={[
              styles.applyButton,
              {
                backgroundColor:
                  !code.trim() || isValidating || disabled
                    ? colors.surface.secondary
                    : colors.primary[500],
              },
            ]}
          >
            {isValidating ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <Text
                style={[
                  styles.applyButtonText,
                  {
                    color:
                      !code.trim() || disabled
                        ? colors.text.tertiary
                        : '#fff',
                  },
                ]}
              >
                Apply
              </Text>
            )}
          </AnimatedPressable>
        </View>

        {/* Error Message */}
        {error && (
          <Animated.View entering={FadeIn.duration(200)}>
            <Text style={[styles.errorText, { color: colors.error }]}>
              {error}
            </Text>
          </Animated.View>
        )}

        {/* Info Text */}
        <Text style={[styles.infoText, { color: colors.text.tertiary }]}>
          Get {REFERRAL_DISCOUNT_PERCENT}% off your first month with a valid referral code
        </Text>
      </View>
    </Animated.View>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles = StyleSheet.create({
  container: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 16,
    gap: 12,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headerTitle: {
    fontSize: 15,
    fontWeight: '600',
  },
  closeButton: {
    padding: 4,
  },
  inputRow: {
    flexDirection: 'row',
    gap: 10,
  },
  input: {
    flex: 1,
    height: 48,
    borderRadius: 12,
    borderWidth: 1,
    paddingHorizontal: 14,
    fontSize: 16,
    fontWeight: '500',
    letterSpacing: 1,
  },
  applyButton: {
    height: 48,
    paddingHorizontal: 20,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  applyButtonText: {
    fontSize: 15,
    fontWeight: '600',
  },
  errorText: {
    fontSize: 13,
    marginTop: -4,
  },
  infoText: {
    fontSize: 12,
  },

  // Toggle button (collapsed state)
  toggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 14,
    borderWidth: 1,
    gap: 12,
  },
  toggleContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flex: 1,
    minWidth: 0,
  },
  toggleIcon: {
    width: 32,
    height: 32,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  toggleText: {
    fontSize: 13,
    flexShrink: 1,
  },
  toggleActionPill: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    flexShrink: 0,
  },
  toggleAction: {
    fontSize: 12,
    fontWeight: '700',
  },
  pendingCodeText: {
    fontSize: 12,
    marginTop: 2,
  },
  applyBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
  },
  applyBadgeText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#fff',
  },

  // Applied state
  appliedContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderRadius: 14,
    borderWidth: 1,
  },
  appliedContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  appliedIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  appliedText: {
    gap: 2,
  },
  appliedTitle: {
    fontSize: 14,
    fontWeight: '600',
  },
  appliedCode: {
    fontSize: 12,
  },
  discountBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
  },
  discountText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#fff',
  },
});
